import { useEffect, useState } from "react";
import Layout from "../components/Layout";
import api, { formatCurrency } from "../lib/api";
import { Gavel, Plus, X, Calendar, TrendingUp, BarChart3 } from "lucide-react";
import { toast, Toaster } from "sonner";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";

export default function Auctions() {
  const [tab, setTab] = useState("listings");
  const [auctions, setAuctions] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [products, setProducts] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [open, setOpen] = useState(false);
  const [closing, setClosing] = useState(null);

  const load = async () => {
    const [a, p, c, an] = await Promise.all([
      api.get("/auctions"),
      api.get("/products"),
      api.get("/crm/customers"),
      api.get("/auctions/analytics"),
    ]);
    setAuctions(a.data);
    setProducts(p.data.filter((x) => x.status !== "Sold"));
    setCustomers(c.data);
    setAnalytics(an.data);
  };
  useEffect(() => { load(); }, []);

  const cancel = async (auction) => {
    if (!window.confirm("Cancel this auction listing?")) return;
    try {
      await api.patch(`/auctions/${auction.id}`, { status: "CANCELLED" });
      toast.success("Listing cancelled, stock available again");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    }
  };

  return (
    <Layout
      title="Auctions"
      subtitle="Listings & sales"
      action={
        <button onClick={() => setOpen(true)} data-testid="new-auction-btn" className="btn-gold px-5 py-3 inline-flex items-center gap-2">
          <Plus className="w-3.5 h-3.5" /> List Piece
        </button>
      }
    >
      <Toaster theme="dark" position="top-right" />

      <div className="flex items-center gap-1 mb-8 border-b border-white/5">
        <TabBtn active={tab === "listings"} onClick={() => setTab("listings")} testid="tab-listings">
          <Gavel className="w-3.5 h-3.5 mr-2 inline" /> Listings
        </TabBtn>
        <TabBtn active={tab === "analytics"} onClick={() => setTab("analytics")} testid="tab-analytics">
          <BarChart3 className="w-3.5 h-3.5 mr-2 inline" /> Analytics
        </TabBtn>
      </div>

      {tab === "analytics" && analytics && <AnalyticsView analytics={analytics} />}

      {tab === "listings" && (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
        {auctions.length === 0 && (
          <div className="col-span-full text-center py-20 glass-card text-[#8A8A8A]">
            <Gavel className="w-8 h-8 mx-auto mb-3 text-[#333]" strokeWidth={1} />
            No active auction listings.
          </div>
        )}
        {auctions.map((a) => (
          <div key={a.id} className="glass-card p-7" data-testid={`auction-${a.id}`}>
            <div className="flex items-start justify-between gap-3 mb-4">
              <div>
                <div className="overline">{a.auction_house || "Internal"}</div>
                <div className="serif text-xl mt-1">{a.listing_name}</div>
                <div className="mono text-xs text-[#D4AF37] mt-1">{a.product_sku}</div>
              </div>
              <span className={`status-badge ${a.status === "SOLD" ? "status-sold" : a.status === "LISTED" ? "status-reserved" : "status-available"}`}>
                {a.status}
              </span>
            </div>

            <div className="text-sm text-[#8A8A8A] mb-4">{a.product_name}</div>

            <div className="space-y-2 mb-5 text-xs">
              <div className="flex justify-between text-[#8A8A8A]">
                <span>Reserve</span>
                <span className="mono text-[#EAEAEA]">{formatCurrency(a.reserve_price)}</span>
              </div>
              {a.final_price && (
                <div className="flex justify-between text-[#7BA585]">
                  <span>Final</span>
                  <span className="mono">{formatCurrency(a.final_price)}</span>
                </div>
              )}
            </div>

            <div className="text-xs text-[#666] mono mb-5 flex items-center gap-2">
              <Calendar className="w-3 h-3" />
              {a.start_date?.slice(0, 10)} → {a.end_date?.slice(0, 10)}
            </div>

            {a.status === "LISTED" && (
              <div className="flex gap-2 pt-4 border-t border-white/5">
                <button
                  data-testid={`close-${a.id}`}
                  onClick={() => setClosing(a)}
                  className="btn-gold flex-1 py-2.5"
                >
                  Mark Sold
                </button>
                <button
                  onClick={() => cancel(a)}
                  className="btn-outline-gold px-4 py-2.5"
                >
                  Cancel
                </button>
              </div>
            )}
          </div>
        ))}
      </div>
      )}

      {open && <NewAuctionModal products={products} onClose={() => setOpen(false)} onSaved={() => { setOpen(false); load(); }} />}
      {closing && <CloseAuctionModal auction={closing} customers={customers} onClose={() => setClosing(null)} onSaved={() => { setClosing(null); load(); }} />}
    </Layout>
  );
}

function TabBtn({ active, onClick, children, testid }) {
  return (
    <button
      data-testid={testid}
      onClick={onClick}
      className={`px-5 py-3 text-xs uppercase tracking-[0.18em] relative ${active ? "text-[#D4AF37]" : "text-[#8A8A8A] hover:text-[#EAEAEA]"}`}
    >
      {children}
      {active && <span className="absolute bottom-0 left-0 right-0 h-px bg-[#D4AF37]" />}
    </button>
  );
}

function AnalyticsView({ analytics }) {
  return (
    <div className="space-y-8" data-testid="analytics-view">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-5">
        <div className="glass-card p-7">
          <div className="overline">Hammer Revenue</div>
          <div className="serif text-3xl mt-3 text-[#D4AF37]" data-testid="analytics-revenue">
            {formatCurrency(analytics.total_hammer_revenue)}
          </div>
          <div className="mono text-xs text-[#8A8A8A] mt-2">{analytics.sold_count} hammered</div>
        </div>
        <div className="glass-card p-7">
          <div className="overline">Win Rate</div>
          <div className="serif text-3xl mt-3 text-[#7BA585]" data-testid="analytics-winrate">{analytics.win_rate}%</div>
          <div className="mono text-xs text-[#8A8A8A] mt-2">
            {analytics.sold_count} sold · {analytics.cancelled_count} cancelled
          </div>
        </div>
        <div className="glass-card p-7">
          <div className="overline flex items-center gap-1.5"><TrendingUp className="w-3 h-3" /> Premium vs Reserve</div>
          <div className={`serif text-3xl mt-3 ${analytics.avg_premium_pct >= 0 ? "text-[#7BA585]" : "text-red-400"}`}>
            {analytics.avg_premium_pct > 0 ? "+" : ""}{analytics.avg_premium_pct}%
          </div>
          <div className="mono text-xs text-[#8A8A8A] mt-2">Avg hammer vs reserve</div>
        </div>
        <div className="glass-card p-7">
          <div className="overline">Active Listings</div>
          <div className="serif text-3xl mt-3">{analytics.listed_count}</div>
          <div className="mono text-xs text-[#8A8A8A] mt-2">of {analytics.total} total</div>
        </div>
      </div>

      {/* By House */}
      <div className="glass-card p-7 lg:p-10">
        <div className="overline">Performance</div>
        <h2 className="serif text-2xl mt-1 mb-6">By Auction House</h2>
        {analytics.by_house.length === 0 ? (
          <div className="text-sm text-[#8A8A8A]">No data yet.</div>
        ) : (
          <>
            <div className="h-64 mb-6" style={{ minHeight: 240 }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={analytics.by_house}>
                  <CartesianGrid strokeDasharray="3 6" stroke="rgba(255,255,255,0.04)" />
                  <XAxis dataKey="house" stroke="#666" fontSize={11} tickLine={false} axisLine={false} />
                  <YAxis stroke="#666" fontSize={11} tickLine={false} axisLine={false} />
                  <Tooltip contentStyle={{ background: "#111", border: "1px solid #333", fontSize: 12 }} />
                  <Bar dataKey="hammer_total" fill="#D4AF37" name="Hammer Revenue" />
                </BarChart>
              </ResponsiveContainer>
            </div>
            <div className="overflow-x-auto -mx-2 px-2">
              <table className="w-full text-sm min-w-[560px]">
              <thead>
                <tr className="text-left overline text-[0.65rem] border-b border-white/5">
                  <th className="py-3 font-normal">House</th>
                  <th className="py-3 font-normal text-right">Listed</th>
                  <th className="py-3 font-normal text-right">Sold</th>
                  <th className="py-3 font-normal text-right">Cancelled</th>
                  <th className="py-3 font-normal text-right">Hammer Total</th>
                </tr>
              </thead>
              <tbody>
                {analytics.by_house.map((h) => (
                  <tr key={h.house} className="border-b border-white/5 last:border-0">
                    <td className="py-3">{h.house}</td>
                    <td className="py-3 mono text-right">{h.listed}</td>
                    <td className="py-3 mono text-right text-[#7BA585]">{h.sold}</td>
                    <td className="py-3 mono text-right text-[#C99356]">{h.cancelled}</td>
                    <td className="py-3 mono text-right text-[#D4AF37]">{formatCurrency(h.hammer_total)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
          </>
        )}
      </div>

      {/* Recent Sold */}
      {analytics.recent_sold.length > 0 && (
        <div className="glass-card p-5 sm:p-7 lg:p-10">
          <div className="overline">Latest Hammers</div>
          <h2 className="serif text-2xl mt-1 mb-6">Recent Sold</h2>
          <div className="overflow-x-auto -mx-2 px-2">
            <table className="w-full text-sm min-w-[560px]">
            <thead>
              <tr className="text-left overline text-[0.65rem] border-b border-white/5">
                <th className="py-3 font-normal">Listing</th>
                <th className="py-3 font-normal">House</th>
                <th className="py-3 font-normal text-right">Reserve</th>
                <th className="py-3 font-normal text-right">Hammer</th>
                <th className="py-3 font-normal text-right">Premium</th>
              </tr>
            </thead>
            <tbody>
              {analytics.recent_sold.map((a) => {
                const premium = a.reserve_price ? ((a.final_price / a.reserve_price - 1) * 100).toFixed(1) : 0;
                return (
                  <tr key={a.id} className="border-b border-white/5 last:border-0">
                    <td className="py-3">{a.listing_name}</td>
                    <td className="py-3 text-[#8A8A8A]">{a.auction_house || "Internal"}</td>
                    <td className="py-3 mono text-right">{formatCurrency(a.reserve_price)}</td>
                    <td className="py-3 mono text-right text-[#D4AF37]">{formatCurrency(a.final_price)}</td>
                    <td className={`py-3 mono text-right ${premium >= 0 ? "text-[#7BA585]" : "text-red-400"}`}>
                      {premium > 0 ? "+" : ""}{premium}%
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          </div>
        </div>
      )}
    </div>
  );
}

function NewAuctionModal({ products, onClose, onSaved }) {
  const [form, setForm] = useState({
    product_id: "",
    listing_name: "",
    reserve_price: 0,
    start_date: new Date().toISOString().slice(0, 10),
    end_date: new Date(Date.now() + 14 * 86400000).toISOString().slice(0, 10),
    auction_house: "",
  });

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/auctions", {
        ...form,
        reserve_price: Number(form.reserve_price),
        start_date: new Date(form.start_date).toISOString(),
        end_date: new Date(form.end_date).toISOString(),
      });
      toast.success("Listed — stock reserved");
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    }
  };

  return (
    <Modal title="List a Piece" subtitle="Auction stage" onClose={onClose}>
      <form onSubmit={submit} className="px-7 py-6 space-y-4" data-testid="auction-form">
        <Field label="Piece">
          <select required value={form.product_id} onChange={(e) => setForm({ ...form, product_id: e.target.value })} className="luxe-input w-full px-4 py-2.5 text-sm" data-testid="auction-product">
            <option value="">— Select —</option>
            {products.map((p) => <option key={p.id} value={p.id}>{p.sku} · {p.name}</option>)}
          </select>
        </Field>
        <Field label="Listing Name">
          <input required value={form.listing_name} onChange={(e) => setForm({ ...form, listing_name: e.target.value })} className="luxe-input w-full px-4 py-2.5 text-sm" placeholder="e.g. Magnificent Jewels — Spring Sale" />
        </Field>
        <Field label="Auction House">
          <input value={form.auction_house} onChange={(e) => setForm({ ...form, auction_house: e.target.value })} className="luxe-input w-full px-4 py-2.5 text-sm" placeholder="Christie's, Sotheby's…" />
        </Field>
        <Field label="Reserve Price (IDR)">
          <input required type="number" step="1" value={form.reserve_price} onChange={(e) => setForm({ ...form, reserve_price: e.target.value })} className="luxe-input w-full px-4 py-2.5 text-sm" />
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Start">
            <input required type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} className="luxe-input w-full px-4 py-2.5 text-sm" />
          </Field>
          <Field label="End">
            <input required type="date" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} className="luxe-input w-full px-4 py-2.5 text-sm" />
          </Field>
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <button type="button" onClick={onClose} className="btn-outline-gold px-5 py-2.5">Cancel</button>
          <button type="submit" data-testid="submit-auction" className="btn-gold px-5 py-2.5">List & Reserve</button>
        </div>
      </form>
    </Modal>
  );
}

function CloseAuctionModal({ auction, customers, onClose, onSaved }) {
  const [final_price, setPrice] = useState(auction.reserve_price);
  const [winner_customer_id, setWinner] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.patch(`/auctions/${auction.id}`, {
        status: "SOLD",
        final_price: Number(final_price),
        winner_customer_id,
      });
      toast.success("Marked SOLD — sale recorded");
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    }
  };

  return (
    <Modal title="Hammer Down" subtitle={auction.listing_name} onClose={onClose}>
      <form onSubmit={submit} className="px-7 py-6 space-y-4" data-testid="close-auction-form">
        <Field label="Final Price (IDR)">
          <input required type="number" step="1" value={final_price} onChange={(e) => setPrice(e.target.value)} className="luxe-input w-full px-4 py-2.5 text-sm" />
        </Field>
        <Field label="Winner (Customer)">
          <select required value={winner_customer_id} onChange={(e) => setWinner(e.target.value)} className="luxe-input w-full px-4 py-2.5 text-sm">
            <option value="">— Select —</option>
            {customers.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </Field>
        <div className="flex justify-end gap-2 pt-2">
          <button type="button" onClick={onClose} className="btn-outline-gold px-5 py-2.5">Cancel</button>
          <button type="submit" data-testid="submit-close" className="btn-gold px-5 py-2.5">Confirm Sold</button>
        </div>
      </form>
    </Modal>
  );
}

function Modal({ title, subtitle, children, onClose }) {
  return (
    <div className="fixed inset-0 z-50 bg-black/80 flex items-start sm:items-center justify-center p-4 overflow-y-auto">
      <div className="relative bg-[#0F0F0F] border border-white/10 w-full max-w-lg fade-up">
        <div className="border-b border-white/5 px-7 py-5 flex justify-between items-center">
          <div>
            <div className="overline">{subtitle}</div>
            <h3 className="serif text-2xl mt-1">{title}</h3>
          </div>
          <button onClick={onClose}><X className="w-5 h-5 text-[#8A8A8A]" /></button>
        </div>
        {children}
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <div className="overline mb-2">{label}</div>
      {children}
    </div>
  );
}
